import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAccounts, createAccount, updateAccount, deleteAccount } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { AlertCircle } from "lucide-react";
import LockedFeatureGate from "../components/LockedFeatureGate";

const EMPTY = { account_id: "", label: "", broker: "", server: "", login: "", password: "", lot_multiplier: 1.0 };

export default function Accounts() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── All hooks MUST be at the top before any conditional returns ──────────
  const [accounts, setAccounts] = useState([]);
  const [form, setForm]         = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [toast, setToast]       = useState(null);

  // Accordion and filter states
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedServer, setSelectedServer] = useState("");
  const [expandedLicenses, setExpandedLicenses] = useState({});

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = () => fetchAccounts().then(setAccounts).catch(console.error);
  useEffect(() => { load(); }, []);

  // ── Access gate (computed AFTER all hooks) ───────────────────────────────
  const isExpired = user?.service_end ? new Date() > new Date(user.service_end) : false;
  const hasNoAccess = user?.role !== "admin" && (isExpired || user?.mt5_per_license_allowed === 0);

  if (hasNoAccess) {
    return <LockedFeatureGate featureName="Trading Accounts Connection" navigate={navigate} />;
  }

  const handleAddAccountClick = () => {
    const maxConnections = user?.mt5_per_license_allowed || 999;
    const activeCount = accounts.filter(a => a.active).length;
    if (user?.role !== "admin" && activeCount >= maxConnections) {
      setShowUpgradeModal(true);
    } else {
      setShowForm(!showForm);
    }
  };

  const handleSubmit = async () => {
    try {
      await createAccount({ ...form, login: Number(form.login), lot_multiplier: Number(form.lot_multiplier) });
      showToast("Account added successfully");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || "Error adding account", "error");
    }
  };

  const toggleActive = async (acc) => {
    try {
      await updateAccount(acc.account_id, { active: !acc.active });
      load();
    } catch { showToast("Failed to update", "error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    try {
      await deleteAccount(id);
      showToast("Account deleted");
      load();
    } catch { showToast("Delete failed", "error"); }
  };

  const toggleExpand = (lic) => {
    setExpandedLicenses(prev => ({ ...prev, [lic]: !prev[lic] }));
  };

  // Dynamic filter values derived from active dataset
  const uniqueClients = Array.from(new Set(accounts.map(a => a.user_name || "Unknown Client"))).filter(Boolean).sort();
  const uniqueServers = Array.from(new Set(accounts.map(a => a.server || "Unknown Server"))).filter(Boolean).sort();

  // Apply filters
  const filteredAccounts = accounts.filter(a => {
    if (selectedClient && (a.user_name || "Unknown Client") !== selectedClient) return false;
    if (selectedServer && (a.server || "Unknown Server") !== selectedServer) return false;
    return true;
  });

  // Group accounts by License ID
  const grouped = {};
  filteredAccounts.forEach(a => {
    const lic = a.license_id || "Manual / Unregistered";
    if (!grouped[lic]) {
      grouped[lic] = {
        license_id: lic,
        user_name: a.user_name || "Unknown User",
        accounts: []
      };
    }
    grouped[lic].accounts.push(a);
  });
  const groupedList = Object.values(grouped);

  return (
    <div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      <div className="page-header">
        <div>
          <div className="page-title">MT5 Accounts</div>
          <div className="page-sub">Manage connected MT5 trading accounts</div>
        </div>
        <button className="btn btn-primary" onClick={handleAddAccountClick}>
          {showForm ? "✕ Cancel" : "+ Add Account"}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 16 }}>New MT5 Account</div>
          <div className="grid-3">
            {[
              { key: "account_id", label: "Account ID (unique)", ph: "acc_001" },
              { key: "label",      label: "Label",               ph: "My ICMarkets" },
              { key: "broker",     label: "Broker",              ph: "ICMarkets" },
              { key: "server",     label: "Server",              ph: "ICMarketsSC-Live" },
              { key: "login",      label: "Login #",             ph: "12345678" },
              { key: "lot_multiplier", label: "Lot Multiplier",  ph: "1.0" },
            ].map(({ key, label, ph }) => (
              <div className="form-group" key={key}>
                <label className="form-label">{label}</label>
                <input className="form-input" placeholder={ph} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" placeholder="MT5 account password"
              value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
          </div>
          <button className="btn btn-primary" onClick={handleSubmit}>Save Account</button>
        </div>
      )}

      {/* Admin Filters */}
      {user?.role === "admin" && (
        <div className="card" style={{ padding: 18, marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 220, flex: "1 1 0" }}>
              <label className="form-label" style={{ fontWeight: 600, color: "var(--accent)" }}>Filter by Client</label>
              <select
                className="form-input"
                style={{ background: "var(--bg)", borderColor: "var(--border)", height: 38, cursor: "pointer" }}
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="">All Clients</option>
                {uniqueClients.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0, minWidth: 220, flex: "1 1 0" }}>
              <label className="form-label" style={{ fontWeight: 600, color: "var(--accent)" }}>Filter by Server Name</label>
              <select
                className="form-input"
                style={{ background: "var(--bg)", borderColor: "var(--border)", height: 38, cursor: "pointer" }}
                value={selectedServer}
                onChange={(e) => setSelectedServer(e.target.value)}
              >
                <option value="">All Servers</option>
                {uniqueServers.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {(selectedClient || selectedServer) && (
              <button
                className="btn btn-ghost"
                style={{ marginTop: 22, height: 38 }}
                onClick={() => {
                  setSelectedClient("");
                  setSelectedServer("");
                }}
              >
                ✕ Clear Filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Accordion License Grouping */}
      {groupedList.length === 0 ? (
        <div className="card empty">No accounts found matching filters — apply the EA inside your MT5 terminal to auto-register.</div>
      ) : (
        groupedList.map(group => {
          const isExpanded = !!expandedLicenses[group.license_id];
          const accs = group.accounts;
          return (
            <div key={group.license_id} className="card" style={{ marginBottom: 15, padding: 0, overflow: "hidden" }}>
              {/* Accordion Header */}
              <div 
                style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  padding: "16px 20px", 
                  background: "var(--card-bg, #161b22)", 
                  cursor: "pointer",
                  borderBottom: isExpanded ? "1px solid var(--border)" : "none"
                }}
                onClick={() => toggleExpand(group.license_id)}
              >
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--accent)", letterSpacing: "0.5px" }}>
                    🔑 {group.license_id}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-muted, #8b949e)", fontWeight: 500 }}>
                    Client Name: <span style={{ color: "var(--text)", fontWeight: 600 }}>{group.user_name}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span className="badge badge-pending" style={{ fontSize: 11, padding: "3px 8px" }}>
                    {accs.length} {accs.length === 1 ? "MT5 Connection" : "MT5 Connections"}
                  </span>
                  <span style={{ transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", color: "var(--text-muted)", fontSize: 10 }}>
                    ▶
                  </span>
                </div>
              </div>

              {/* Accordion Content */}
              {isExpanded && (
                <div style={{ overflowX: "auto", padding: "10px 20px 20px" }}>
                  <table style={{ margin: "10px 0 0" }}>
                    <thead>
                      <tr>
                        <th>MT5 Account ID</th>
                        <th>Account Name</th>
                        <th>Server Name</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accs.map(a => (
                        <tr key={a.account_id}>
                          <td style={{ fontWeight: 600 }}>{a.account_id}</td>
                          <td>{a.name || "—"}</td>
                          <td>{a.server || "—"}</td>
                          <td>
                            <span className={`badge ${a.active ? "badge-ok" : "badge-closed"}`}>
                              {a.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-ghost" style={{ marginRight: 6, fontSize: 11 }} onClick={() => toggleActive(a)}>
                              {a.active ? "Disable" : "Enable"}
                            </button>
                            <button className="btn btn-danger" style={{ fontSize: 11 }} onClick={() => handleDelete(a.account_id)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}
      {showUpgradeModal && (
        <div className="checkout-modal-overlay">
          <div className="checkout-modal" style={{ maxWidth: "440px" }}>
            <div className="checkout-header">
              <h4 className="checkout-title" style={{ color: "var(--red)" }}>Upgrade Plan Required</h4>
              <button className="close-modal-btn" onClick={() => setShowUpgradeModal(false)}>✕</button>
            </div>
            <div className="checkout-content" style={{ textAlign: "center", padding: "30px 24px" }}>
              <div className="upgrade-warning-icon">
                <AlertCircle size={30} />
              </div>
              <h3 style={{ fontSize: "18px", fontWeight: "800", color: "var(--text)", marginBottom: "12px" }}>
                Limit Reached
              </h3>
              <p style={{ color: "var(--text2)", fontSize: "14px", lineHeight: "1.5", marginBottom: "24px" }}>
                You have reached the maximum allowed limit for your active <strong>{user?.service_plan?.toUpperCase() || "DEMO"}</strong> package. 
                To unlock more license IDs, MT5 account connections, and TradingView strategy integrations, please upgrade your plan.
              </p>
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", marginBottom: "24px", textAlign: "left" }}>
                <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--accent)", marginBottom: "8px" }}>Active Package Limits:</div>
                <div style={{ fontSize: "12.5px", color: "var(--text2)", display: "flex", flexDirection: "column", gap: "6px" }}>
                  <div>🔑 License IDs Allowed: <strong>{user?.allowed_licenses || 3}</strong></div>
                  <div>📡 MT5 Connections / License: <strong>{user?.mt5_per_license_allowed || 5}</strong></div>
                </div>
              </div>
              <button
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center", padding: "12px" }}
                onClick={() => { setShowUpgradeModal(false); navigate("/renew-plan"); }}
              >
                Upgrade / Renew Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
